import { cs } from "../../utils/currency";
import { printReceiptQz } from "./qzPrinter";
import {
  generateOrderBillPdf,
  ReceiptOrder,
} from "./receiptPrint";
import {
  printKitchenTicketsBluetooth,
  printReceiptBluetooth,
  printReceiptRawBT,
} from "./bluetoothPrinter";

export type PrintMethod = "qz" | "rawbt" | "bluetooth" | "system";

const METHOD_KEY = "pos_print_method";

export const getPrintMethod = (): PrintMethod =>
  (localStorage.getItem(METHOD_KEY) as PrintMethod) || "rawbt";

export const setPrintMethod = (method: PrintMethod): void =>
  localStorage.setItem(METHOD_KEY, method);

/**
 * Fire the RawBT intent and detect whether the app actually opened.
 *
 * When Chrome on Android handles an intent URL with a specific package name
 * (package=ru.a402d.rawbtprinter):
 *   • Package installed   → Android switches to the app → visibilitychange → "hidden"
 *   • Package not found   → Chrome shows an inline error toast and stays in the
 *                           foreground (no OS-level dialog, no visibility change)
 *
 * So: if the page goes hidden at all → RawBT opened → success.
 *     If the page never goes hidden within 2 s → not installed → fail.
 */
const tryRawBT = (
  order: ReceiptOrder,
  logoBase64?: string,
  currencySymbol = cs(),
): Promise<boolean> =>
  new Promise((resolve) => {
    let settled = false;

    const settle = (result: boolean) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(noHideTimer);
      resolve(result);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") settle(true);
    };

    // If the page never goes hidden within 3 s → package not installed or intent blocked
    const noHideTimer = setTimeout(() => settle(false), 3000);

    document.addEventListener("visibilitychange", onVisibility);
    printReceiptRawBT(order, undefined, logoBase64, currencySymbol);
  });

/**
 * Universal print — selects the best available method in this order:
 *   1. QZ Tray           (desktop, silent)
 *   2. RawBT             (Android, Bluetooth Classic SPP via intent URL — recommended)
 *   3. Web Bluetooth     (Android Chrome BLE only — fallback)
 *   4. System print dialog (iOS + universal fallback)
 *
 * The preferred method is read from localStorage key `pos_print_method`.
 */
export const printUniversal = async (
  order: ReceiptOrder,
  logoBase64?: string,
  receiptLogoUrl?: string,
  currencySymbol = cs(),
  receiptOnly = false,
): Promise<PrintMethod> => {
  const preferred = getPrintMethod();

  // ── 1. QZ Tray ────────────────────────────────────────────────────────────
  if (preferred === "qz") {
    try {
      await printReceiptQz(order, undefined, logoBase64, currencySymbol);
      return "qz";
    } catch (err) {
      console.warn("[UniversalPrint] QZ Tray unavailable:", err);
    }
  }

  // ── 2. RawBT (Android Bluetooth Classic via Intent URL) ───────────────────
  if (preferred === "rawbt") {
    const launched = await tryRawBT(order, logoBase64, currencySymbol);
    if (launched) return "rawbt";
    throw new Error("RawBT did not open — app may not be installed or intent was blocked");
  }

  // ── 3. Web Bluetooth BLE (Android Chrome) ─────────────────────────────────
  if (preferred === "bluetooth") {
    await printReceiptBluetooth(order, undefined, logoBase64, currencySymbol, receiptOnly);
    return "bluetooth";
  }

  // ── 4. System print dialog (iOS AirPrint / any platform) ─────────────────
  if (preferred === "system") {
    generateOrderBillPdf({ order, logoUrl: receiptLogoUrl, currencySymbol });
    return "system";
  }

  // Fallback — should not reach here if a valid method is set
  throw new Error(`Unknown print method: ${preferred}`);
};

/**
 * Prints per-item kitchen tickets to the dedicated kitchen BLE printer.
 * Kitchen always uses BLE (independent of the sales print method).
 *
 * Throws "NO_KITCHEN_PRINTER" if no kitchen printer has been paired yet —
 * callers should catch this and show <KitchenPrinterModal />.
 */
export const printKitchenUniversal = async (
  order: ReceiptOrder,
): Promise<void> => {
  // printKitchenTicketsBluetooth throws "NO_KITCHEN_PRINTER" when unpaired
  await printKitchenTicketsBluetooth(order);
};
